import type {
  BobbinAppendIntent,
  BobbinDailyAppend,
  BobbinMobileSession,
  BobbinMobileTool,
  BobbinToolArguments,
  BobbinToolResults,
} from "./types";

const THREAD_API_BASE = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");
const BOBBIN_MOBILE_API_BASE = (
  import.meta.env.VITE_BOBBIN_MOBILE_API_URL ?? `${THREAD_API_BASE}/bobbin/mobile`
).replace(/\/$/, "");

export const BOBBIN_MOBILE_ENDPOINTS = {
  session: `${BOBBIN_MOBILE_API_BASE}/session`,
  mcp: `${BOBBIN_MOBILE_API_BASE}/mcp`,
} as const;

export const BOBBIN_MOBILE_TOOL_NAMES = {
  list_activity: "list_activity",
  list_sessions: "list_sessions",
  list_attention: "list_attention",
  search: "search",
  node_lookup: "node_lookup",
  node_content: "node_content",
  graph_walk: "graph_walk",
  daily_get: "daily_get",
  daily_append: "daily_append",
} as const satisfies Record<BobbinMobileTool, string>;

type JsonRecord = Record<string, unknown>;

interface McpStructuredContent<T> {
  schema_version?: string;
  tool?: string;
  data?: T;
  error?: {
    code?: string;
    retryable?: boolean;
  };
}

interface McpToolResult<T> {
  structuredContent?: McpStructuredContent<T>;
  isError?: boolean;
}

interface McpResponse<T> {
  jsonrpc: "2.0";
  id: number;
  result?: McpToolResult<T>;
  error?: { code?: number };
}

export class BobbinMobileError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryable: boolean;

  constructor({
    status,
    code,
    retryable,
  }: {
    status: number;
    code: string;
    retryable: boolean;
  }) {
    super(safeErrorMessage(status, code));
    this.name = "BobbinMobileError";
    this.status = status;
    this.code = code;
    this.retryable = retryable;
  }
}

function safeErrorMessage(status: number, code: string): string {
  if (status === 0) return "Bobbin could not be reached. Check your connection and try again.";
  if (status === 401) return "This iPhone is not enrolled, or its session has expired.";
  if (status === 403) return "This Bobbin session is not allowed to perform that action.";
  if (status === 409 || code.includes("CONFLICT") || code.includes("IDEMPOTENCY")) {
    return "Bobbin rejected a conflicting retry. Nothing was overwritten.";
  }
  if (status === 429) return "Bobbin is busy. Try again in a moment.";
  if (status >= 500) return "Bobbin is temporarily unavailable. Your request can be retried safely.";
  return "Bobbin could not complete that request.";
}

function isRetryableStatus(status: number): boolean {
  return status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;
}

function safeErrorEnvelope(value: unknown): { code: string; retryable?: boolean } {
  if (!value || typeof value !== "object") return { code: "BOBBIN_HTTP_ERROR" };
  const object = value as JsonRecord;
  const detail = object.detail;
  if (!detail || typeof detail !== "object") return { code: "BOBBIN_HTTP_ERROR" };
  const safeDetail = detail as JsonRecord;
  return {
    code: typeof safeDetail.code === "string" ? safeDetail.code : "BOBBIN_HTTP_ERROR",
    retryable: typeof safeDetail.retryable === "boolean" ? safeDetail.retryable : undefined,
  };
}

async function responseError(response: Response): Promise<BobbinMobileError> {
  let envelope: { code: string; retryable?: boolean } = { code: "BOBBIN_HTTP_ERROR" };
  try {
    envelope = safeErrorEnvelope(await response.json());
  } catch {
    // Deliberately ignore response text: it may contain private Daily content.
  }
  return new BobbinMobileError({
    status: response.status,
    code: envelope.code,
    retryable: envelope.retryable ?? isRetryableStatus(response.status),
  });
}

async function requestJson<T>(
  url: string,
  init: RequestInit,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      cache: "no-store",
      credentials: "include",
      referrerPolicy: "strict-origin",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-store",
        ...init.headers,
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new BobbinMobileError({
      status: 0,
      code: "BOBBIN_TRANSPORT_ERROR",
      retryable: true,
    });
  }
  if (!response.ok) throw await responseError(response);
  if (response.status === 204) return undefined as T;
  try {
    return await response.json() as T;
  } catch {
    throw new BobbinMobileError({
      status: 502,
      code: "BOBBIN_INVALID_JSON",
      retryable: true,
    });
  }
}

let rpcSequence = 0;

function nextRpcId(): number {
  rpcSequence = (rpcSequence + 1) % Number.MAX_SAFE_INTEGER;
  return rpcSequence;
}

function sessionResponse(value: BobbinMobileSession): BobbinMobileSession {
  if (!value.authenticated) {
    return { authenticated: false, device: null, csrf_token: null };
  }
  const { id, name, expires_at, created_at, last_used_at } = value.device;
  if (
    !id || !name || !expires_at || !value.csrf_token
    || [id, name, expires_at, value.csrf_token].some((item) => item.includes("bmb_"))
  ) {
    throw new BobbinMobileError({
      status: 502,
      code: "BOBBIN_SESSION_ENVELOPE_ERROR",
      retryable: false,
    });
  }
  return {
    authenticated: true,
    device: { id, name, expires_at, created_at, last_used_at },
    csrf_token: value.csrf_token,
  };
}

export class BobbinMobileClient {
  async getSession(signal?: AbortSignal): Promise<BobbinMobileSession> {
    try {
      const response = await requestJson<BobbinMobileSession>(BOBBIN_MOBILE_ENDPOINTS.session, {
        method: "GET",
        signal,
      });
      return sessionResponse(response);
    } catch (error) {
      if (error instanceof BobbinMobileError && error.status === 401) {
        return { authenticated: false, device: null, csrf_token: null };
      }
      throw error;
    }
  }

  enroll(
    deviceName: string,
    oidcAccessToken: string,
    signal?: AbortSignal,
  ): Promise<BobbinMobileSession> {
    return requestJson<BobbinMobileSession>(BOBBIN_MOBILE_ENDPOINTS.session, {
      method: "POST",
      signal,
      headers: {
        Authorization: `Bearer ${oidcAccessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ device_name: deviceName }),
    }).then(sessionResponse);
  }

  async revoke(csrfToken: string, signal?: AbortSignal): Promise<void> {
    await requestJson<unknown>(BOBBIN_MOBILE_ENDPOINTS.session, {
      method: "DELETE",
      signal,
      headers: { "X-Bobbin-Mobile-CSRF": csrfToken },
    });
  }

  async callTool<K extends BobbinMobileTool>(
    tool: K,
    args: BobbinToolArguments[K],
    csrfToken: string,
    signal?: AbortSignal,
  ): Promise<BobbinToolResults[K]> {
    const response = await requestJson<McpResponse<BobbinToolResults[K]>>(
      BOBBIN_MOBILE_ENDPOINTS.mcp,
      {
        method: "POST",
        signal,
        headers: {
          "Content-Type": "application/json",
          "X-Bobbin-Mobile-CSRF": csrfToken,
        },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: nextRpcId(),
          method: "tools/call",
          params: {
            name: BOBBIN_MOBILE_TOOL_NAMES[tool],
            arguments: args,
          },
        }),
      },
    );
    const structured = response.result?.structuredContent;
    if (response.error || !response.result || !structured) {
      throw new BobbinMobileError({
        status: 502,
        code: "BOBBIN_MCP_ENVELOPE_ERROR",
        retryable: true,
      });
    }
    if (response.result.isError || structured.error) {
      throw new BobbinMobileError({
        status: 200,
        code: structured.error?.code ?? "BOBBIN_TOOL_ERROR",
        retryable: structured.error?.retryable ?? false,
      });
    }
    if (structured.tool && structured.tool !== BOBBIN_MOBILE_TOOL_NAMES[tool]) {
      throw new BobbinMobileError({
        status: 502,
        code: "BOBBIN_MCP_TOOL_MISMATCH",
        retryable: true,
      });
    }
    if (!("data" in structured)) {
      throw new BobbinMobileError({
        status: 502,
        code: "BOBBIN_MCP_DATA_MISSING",
        retryable: true,
      });
    }
    return structured.data as BobbinToolResults[K];
  }

  async appendDailyWithRetry(
    intent: BobbinAppendIntent,
    csrfToken: string,
    signal?: AbortSignal,
  ): Promise<BobbinDailyAppend> {
    try {
      return await this.callTool("daily_append", intent, csrfToken, signal);
    } catch (error) {
      if (!(error instanceof BobbinMobileError) || !error.retryable || signal?.aborted) {
        throw error;
      }
      return this.callTool("daily_append", intent, csrfToken, signal);
    }
  }
}

export const bobbinMobileClient = new BobbinMobileClient();
